import {
  Badge, Box, BreadcrumbGroup, Button, ButtonDropdown, ColumnLayout, Container, Flashbar,
  Form, FormField, Header, Input, Autosuggest, Modal, ProgressBar, Select, SpaceBetween, Spinner,
  StatusIndicator, Table, Tabs, Toggle, Alert,
} from '@cloudscape-design/components';
import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getAuthToken } from './auth';
import { useDocumentTitle } from './useDocumentTitle';
import { getAwsCreds, getDefaultRegion } from './awsCredentials';
import { getJiraCreds } from './jiraCredentials';
import { API_BASE_URL } from './config';
import EnvironmentCards from './EnvironmentCards';
import ProjectFormModal from './ProjectFormModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import DeleteStackModal from './DeleteStackModal';
import { apiFetch as sharedApiFetch } from './apiFetch';

// Common environment names offered as suggestions when creating an environment. The field still
// accepts any freeform name (e.g. a custom environment specific to this project) — these are
// just the most frequently used ones, shown first for convenience.
const COMMON_ENV_NAMES = ['Dev', 'Qa', 'Staging', 'Prod'];

interface Environment { documentId: string; name: string; isProduction: boolean; }
interface StackAccount { documentId: string; Alias: string; accountid: string; }
interface Stack { documentId: string; name: string; stack_status: string; Region: string; arn: string; environment?: Environment | null; account?: StackAccount | null; }
interface Stakeholder { documentId: string; firstName: string; lastName: string; email: string; department: string; role: string; }
interface StakeholderGroup { documentId: string; name: string; description: string; stakeholders: Stakeholder[]; }
interface JiraIssue { key: string; summary: string; status?: string; assignee: string | null; type?: string; updated: string; }

function statusType(s: string): 'success' | 'error' | 'in-progress' | 'warning' | 'info' {
  if (/COMPLETE$/.test(s) && !/ROLLBACK|DELETE/.test(s)) return 'success';
  if (/_FAILED$/.test(s)) return 'error';
  if (/_IN_PROGRESS$/.test(s)) return 'in-progress';
  if (/ROLLBACK|DELETE/.test(s)) return 'warning';
  return 'info';
}

function apiFetch(path: string, apiKey: string) {
  return fetch(`${API_BASE_URL}${path}`, { headers: { Authorization: `Bearer ${apiKey}` } })
    .then(r => { if (!r.ok) throw new Error(`API error: ${r.status}`); return r.json(); });
}

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiKey = getAuthToken();

  const [project, setProject] = useState<any>(null);
  useDocumentTitle(project?.name);
  const [stacks, setStacks] = useState<Stack[]>([]);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [selectedEnvId, setSelectedEnvId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createEnvVisible, setCreateEnvVisible] = useState(false);
  const [createEnvName, setCreateEnvName] = useState('');
  const [createEnvSaving, setCreateEnvSaving] = useState(false);
  const [createEnvError, setCreateEnvError] = useState<string | null>(null);

  const [moveStackTarget, setMoveStackTarget] = useState<Stack | null>(null);
  const [moveStackEnv, setMoveStackEnv] = useState<{ value: string; label: string } | null>(null);
  const [moveStackSaving, setMoveStackSaving] = useState(false);

  const [diagramLoading, setDiagramLoading] = useState(false);
  const [diagramProgress, setDiagramProgress] = useState<{ percent: number; label: string; detail?: string } | null>(null);

  const [stakeholders, setStakeholders] = useState<Stakeholder[] | null>(null);
  const [groups, setGroups] = useState<StakeholderGroup[] | null>(null);
  const [shLoading, setShLoading] = useState(false);
  const [shError, setShError] = useState<string | null>(null);
  const [shFetched, setShFetched] = useState(false);

  const [products, setProducts] = useState<any[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsFetched, setProductsFetched] = useState(false);

  const [deployments, setDeployments] = useState<any[]>([]);
  const [deploymentsLoading, setDeploymentsLoading] = useState(false);
  const [deploymentsFetched, setDeploymentsFetched] = useState(false);
  const [selectedDeployment, setSelectedDeployment] = useState<any>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [activeTabId, setActiveTabId] = useState('stacks');

  const [jira, setJira] = useState<JiraIssue[]>([]);
  const [jiraTotal, setJiraTotal] = useState(0);
  const [jiraLoading, setJiraLoading] = useState(false);
  const [jiraError, setJiraError] = useState<string | null>(null);
  const [jiraFetched, setJiraFetched] = useState(false);
  const [jiraUnconfigured, setJiraUnconfigured] = useState(false);
  const [jiraBaseUrl, setJiraBaseUrl] = useState('');

  const [allStakeholders, setAllStakeholders] = useState<Stakeholder[]>([]);
  const [allGroups, setAllGroups] = useState<StakeholderGroup[]>([]);
  const [addShVisible, setAddShVisible] = useState(false);
  const [addGrVisible, setAddGrVisible] = useState(false);
  const [selectedSh, setSelectedSh] = useState<Stakeholder[]>([]);
  const [selectedGr, setSelectedGr] = useState<StakeholderGroup[]>([]);
  const [addShLoading, setAddShLoading] = useState(false);

  const [attachLoading, setAttachLoading] = useState(false);
  const [flash, setFlash] = useState<{ type: 'success' | 'error'; content: string } | null>(null);
  const [attachModalVisible, setAttachModalVisible] = useState(false);
  const [attachEnvFilter, setAttachEnvFilter] = useState<Environment | null>(null);
  const [attachAccountOptions, setAttachAccountOptions] = useState<{ value: string; label: string; alias: string }[]>([]);
  const [attachAccount, setAttachAccount] = useState<{ value: string; label: string; alias: string } | null>(null);

  const [editVisible, setEditVisible] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [deleteSaving, setDeleteSaving] = useState(false);

  const [deleteStackTarget, setDeleteStackTarget] = useState<Stack | null>(null);
  const [deleteStackSaving, setDeleteStackSaving] = useState(false);
  const [deleteStackError, setDeleteStackError] = useState<string | null>(null);

  const STACKS_POPULATE = 'populate[stacks][fields][0]=name&populate[stacks][fields][1]=stack_status&populate[stacks][fields][2]=Region&populate[stacks][fields][3]=arn&populate[stacks][populate][environment][fields][0]=name&populate[stacks][populate][environment][fields][1]=isProduction&populate[stacks][populate][account][fields][0]=Alias&populate[stacks][populate][account][fields][1]=accountid&populate[environments][fields][0]=name&populate[environments][fields][1]=isProduction';

  const refetchProject = () => {
    if (!apiKey || !id) return Promise.resolve();
    return apiFetch(`/projects/${id}?populate[account][fields][0]=Alias&populate[account][fields][1]=accountid&${STACKS_POPULATE}&populate[jira_epics][fields][0]=title&populate[jira_epics][fields][1]=url&populate[sciforma_links][fields][0]=title&populate[sciforma_links][fields][1]=url&populate[external_links][fields][0]=title&populate[external_links][fields][1]=url`, apiKey)
      .then(json => {
        setProject(json.data);
        setStacks(json.data.stacks ?? []);
        setEnvironments(json.data.environments ?? []);
      });
  };

  useEffect(() => {
    if (!apiKey || !id) { setLoading(false); return; }
    refetchProject()
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, id]);

  const fetchStakeholders = () => {
    if (shFetched || !apiKey || !id) return;
    setShFetched(true);
    setShLoading(true);
    apiFetch(`/projects/${id}?populate[stakeholders][fields][0]=firstName&populate[stakeholders][fields][1]=lastName&populate[stakeholders][fields][2]=email&populate[stakeholders][fields][3]=department&populate[stakeholders][fields][4]=role&populate[stakeholder_groups][fields][0]=name&populate[stakeholder_groups][fields][1]=description&populate[stakeholder_groups][populate][stakeholders][fields][0]=firstName&populate[stakeholder_groups][populate][stakeholders][fields][1]=lastName`, apiKey)
      .then(json => {
        setStakeholders(json.data.stakeholders ?? []);
        setGroups(json.data.stakeholder_groups ?? []);
      })
      .catch(e => setShError(e.message))
      .finally(() => setShLoading(false));
  };

  const fetchProducts = () => {
    if (productsFetched || !apiKey || !id) return;
    setProductsFetched(true);
    setProductsLoading(true);
    apiFetch(`/projects/${id}/products`, apiKey)
      .then(json => setProducts(json.data ?? json))
      .catch(e => console.log('Failed to fetch products:', e.message))
      .finally(() => setProductsLoading(false));
  };

  // Fetches Jenkins deployment-requests and FM provisioning-requests for this project in
  // parallel, normalizes them into a single row shape, and merges them sorted by submittedAt
  // desc. If the FM call fails we still show the Jenkins rows (and log the error), since FM
  // bundles are optional and shouldn't break the Deployments tab.
  const loadDeployments = useCallback(() => {
    if (!apiKey || !project) return;
    setDeploymentsLoading(true);

    const jenkinsP = apiFetch(`/deployment-requests/by-project/${project.name}`, apiKey)
      .then(json => (json.data ?? json) as any[])
      .catch(e => { console.log('Failed to fetch deployments:', e.message); return [] as any[]; });

    const fmP = apiFetch(`/fm-provisioning-requests/by-project/${project.name}`, apiKey)
      .then(json => (json.data ?? json) as any[])
      .then(rows => rows.map((r: any) => ({
        documentId: r.documentId,
        source: 'fm',
        status: r.status,
        productType: `FM Bundle (${r.action})`,
        requestedBy: r.requestedBy,
        submittedAt: r.submittedAt,
        completedAt: r.completedAt,
        // FM provisioning uses an SSM execution id instead of a Jenkins build.
        executionId: r.executionId,
        statusMessage: r.statusMessage,
        parameters: r.parameters,
        projectSlug: r.fm_bundle?.projectSlug,
      })))
      .catch(e => { console.log('Failed to fetch FM provisioning requests:', e.message); return [] as any[]; });

    Promise.all([jenkinsP, fmP])
      .then(([jenkins, fm]) => {
        const merged = [...jenkins, ...fm].sort((a, b) => {
          const at = new Date(a.submittedAt || 0).getTime();
          const bt = new Date(b.submittedAt || 0).getTime();
          return bt - at;
        });
        setDeployments(merged);
      })
      .finally(() => setDeploymentsLoading(false));
  }, [apiKey, project]);

  const fetchDeployments = () => {
    if (deploymentsFetched || !apiKey || !project) return;
    setDeploymentsFetched(true);
    loadDeployments();
  };

  const fetchJira = () => {
    if (jiraFetched || !apiKey || !project) return;
    setJiraFetched(true);
    const creds = getJiraCreds();
    if (!creds) {
      // Not configured — render an info Alert instead of firing a request.
      setJiraUnconfigured(true);
      return;
    }
    setJiraUnconfigured(false);
    setJiraBaseUrl(creds.baseUrl.replace(/\/$/, ''));
    setJiraLoading(true);
    setJiraError(null);
    fetch(`${API_BASE_URL}/jira/search?projectName=${encodeURIComponent(project.name)}`, {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        'X-Jira-Base-Url': creds.baseUrl,
        'X-Jira-Token': creds.token,
        'X-Jira-Insecure-TLS': creds.insecure ? 'true' : 'false',
      },
    })
      .then(async r => {
        const json = await r.json().catch(() => null);
        if (!r.ok) throw new Error(json?.error?.message || `API error: ${r.status}`);
        return json;
      })
      .then(json => {
        setJira(json.issues ?? []);
        setJiraTotal(json.total ?? 0);
      })
      .catch(e => setJiraError(e.message))
      .finally(() => setJiraLoading(false));
  };

  const refreshDeployments = useCallback(() => {
    loadDeployments();
  }, [loadDeployments]);

  useEffect(() => {
    if (activeTabId !== 'deployments' || !autoRefresh || !project) return;
    const interval = setInterval(refreshDeployments, 120000);
    return () => clearInterval(interval);
  }, [activeTabId, autoRefresh, project, refreshDeployments]);

  const refetchStakeholders = () => {
    if (!apiKey || !id) return;
    setShLoading(true);
    apiFetch(`/projects/${id}?populate[stakeholders][fields][0]=firstName&populate[stakeholders][fields][1]=lastName&populate[stakeholders][fields][2]=email&populate[stakeholders][fields][3]=department&populate[stakeholders][fields][4]=role&populate[stakeholder_groups][fields][0]=name&populate[stakeholder_groups][fields][1]=description&populate[stakeholder_groups][populate][stakeholders][fields][0]=firstName&populate[stakeholder_groups][populate][stakeholders][fields][1]=lastName`, apiKey)
      .then(json => {
        setStakeholders(json.data.stakeholders ?? []);
        setGroups(json.data.stakeholder_groups ?? []);
      })
      .catch(e => setShError(e.message))
      .finally(() => setShLoading(false));
  };

  const openAddStakeholder = () => {
    if (!apiKey) return;
    apiFetch('/stakeholders?pagination[pageSize]=100&fields[0]=firstName&fields[1]=lastName&fields[2]=email&fields[3]=department&fields[4]=role', apiKey)
      .then(json => setAllStakeholders(json.data ?? []))
      .catch(() => {});
    setSelectedSh([]);
    setAddShVisible(true);
  };

  const openAddGroup = () => {
    if (!apiKey) return;
    apiFetch('/stakeholder-groups?pagination[pageSize]=100&fields[0]=name&fields[1]=description', apiKey)
      .then(json => setAllGroups(json.data ?? []))
      .catch(() => {});
    setSelectedGr([]);
    setAddGrVisible(true);
  };

  const linkStakeholders = async () => {
    if (!apiKey || !id) return;
    setAddShLoading(true);
    try {
      const existing = (stakeholders ?? []).map(s => s.documentId);
      const ids = Array.from(new Set([...existing, ...selectedSh.map(s => s.documentId)]));
      await fetch(`${API_BASE_URL}/projects/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
        body: JSON.stringify({ data: { stakeholders: ids } }),
      });
      setShFetched(false);
      refetchStakeholders();
      setAddShVisible(false);
      setFlash({ type: 'success', content: `Linked ${selectedSh.length} stakeholder(s)` });
    } catch (e: any) {
      setFlash({ type: 'error', content: e.message });
    } finally { setAddShLoading(false); }
  };

  const linkGroups = async () => {
    if (!apiKey || !id) return;
    setAddShLoading(true);
    try {
      const existing = (groups ?? []).map(g => g.documentId);
      const ids = Array.from(new Set([...existing, ...selectedGr.map(g => g.documentId)]));
      await fetch(`${API_BASE_URL}/projects/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
        body: JSON.stringify({ data: { stakeholder_groups: ids } }),
      });
      setShFetched(false);
      refetchStakeholders();
      setAddGrVisible(false);
      setFlash({ type: 'success', content: `Linked ${selectedGr.length} group(s)` });
    } catch (e: any) {
      setFlash({ type: 'error', content: e.message });
    } finally { setAddShLoading(false); }
  };

  const openAttachModal = async (envFilter?: Environment) => {
    setAttachEnvFilter(envFilter ?? null);
    setAttachAccount(null);
    try {
      const json = await apiFetch('/accounts', apiKey!);
      setAttachAccountOptions(
        json.data.map((a: any) => ({ value: a.accountid, label: `${a.Alias} (${a.accountid})`, alias: a.Alias }))
      );
    } catch {}
    setAttachModalVisible(true);
  };

  const handleAttachStacks = async () => {
    if (!attachAccount) {
      setFlash({ type: 'error', content: 'Please select an account' });
      return;
    }
    const creds = getAwsCreds(attachAccount.alias);
    if (!creds) {
      setFlash({ type: 'error', content: `No AWS credentials configured for account "${attachAccount.alias}". Please add credentials in Settings.` });
      return;
    }
    setAttachModalVisible(false);
    setAttachLoading(true);
    setFlash(null);
    try {
      const res = await fetch(`${API_BASE_URL}/stacks/import-by-project`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`,
          'x-aws-access-key-id': creds.accessKeyId,
          'x-aws-secret-access-key': creds.secretAccessKey,
          'x-aws-session-token': creds.sessionToken,
        },
        body: JSON.stringify({
          // Every import is always scoped to this project's own ICLProject tag — never
          // replaced by the environment filter — so "Locate stacks for <env>" can only ever
          // match this project's stacks in that environment, not every project's same-named
          // environment (see services/aws-cloudformation.ts TagFilter semantics).
          tagKey: 'ICLProject',
          tagValue: project.name,
          ...(attachEnvFilter ? { extraTagKey: 'ICLEnv', extraTagValue: attachEnvFilter.name } : {}),
          projectId: id,
          accountId: attachAccount.value,
          region: getDefaultRegion(),
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || `API error: ${res.status}`);
      }
      const result = await res.json();
      setFlash({ type: 'success', content: `Attached stacks: ${result.imported} imported, ${result.updated} updated, ${result.errors} errors` });
      const refreshedProject = await apiFetch(`/projects/${id}?${STACKS_POPULATE}`, apiKey!);
      const refreshedStacks = refreshedProject.data.stacks ?? [];
      setStacks(refreshedStacks);
      setEnvironments(refreshedProject.data.environments ?? []);
      // Create workloads for each stack (fail silently)
      for (const stack of refreshedStacks) {
        try {
          await fetch(`${API_BASE_URL}/stacks/${stack.documentId}/create-workloads`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${apiKey}` },
          });
        } catch (e: any) { console.log(`create-workloads failed for ${stack.documentId}:`, e.message); }
      }
      // Refresh products
      setProductsFetched(true);
      setProductsLoading(true);
      apiFetch(`/projects/${id}/products`, apiKey!)
        .then(json => setProducts(json.data ?? json))
        .catch(e => console.log('Failed to fetch products:', e.message))
        .finally(() => setProductsLoading(false));
    } catch (e: any) {
      setFlash({ type: 'error', content: e.message });
    } finally {
      setAttachLoading(false);
    }
  };

  const handleEditProject = async (values: { name: string; description: string; jira_epics: { title: string; url: string }[]; sciforma_links: { title: string; url: string }[]; external_links: { title: string; url: string }[] }) => {
    if (!apiKey || !id) return;
    setEditSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/projects/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
        body: JSON.stringify({ data: { name: values.name, description: values.description || null, jira_epics: values.jira_epics, sciforma_links: values.sciforma_links, external_links: values.external_links } }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || `API error: ${res.status}`);
      }
      await refetchProject();
      setEditVisible(false);
      setFlash({ type: 'success', content: 'Project updated' });
    } catch (e: any) {
      setFlash({ type: 'error', content: e.message });
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!apiKey || !id) return;
    setDeleteSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/projects/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${apiKey}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || `API error: ${res.status}`);
      }
      // The project no longer exists — navigate away rather than refetching it.
      setFlash({ type: 'success', content: 'Project deleted' });
      navigate('/projects');
    } catch (e: any) {
      setFlash({ type: 'error', content: e.message });
      setDeleteSaving(false);
    }
  };

  /**
   * Destroys a stack's underlying AWS infrastructure. There are two possible paths:
   *
   * 1. Jenkins/CDK destroy (preferred, when available): resubmits the stack's most recent
   *    deployment-request payload with `operationType: 'destroy'` instead of the default
   *    'deploy' — this reuses the exact same Jenkins `devops-cdk` pipeline and CDK context as a
   *    normal create/edit submission (see EditProductPage for the equivalent "load last payload"
   *    flow), so the server only has to branch on operationType rather than on a separate destroy
   *    code path. Requires AWS credentials for the "appsb" account (where Jenkins' own API
   *    credentials live, per the AWS credentials convention), not the stack's deployment
   *    account — the deployment account itself is read from the deployment-request's own
   *    `account` relation and sent as `accountId` the same way submit() already expects it.
   *
   * 2. Direct CloudFormation DeleteStack (fallback): used when the stack has no deployment
   *    history in this app (e.g. it was imported from AWS or created outside of ITPMNG), since
   *    there's no stored CDK context to resubmit to Jenkins in that case. This bypasses Jenkins
   *    entirely and uses AWS credentials for the stack's own account instead of 'appsb'.
   */
  const handleDestroyStack = async () => {
    const stack = deleteStackTarget;
    if (!stack) return;
    setDeleteStackError(null);
    setDeleteStackSaving(true);
    try {
      const latestRes = await sharedApiFetch(`/deployment-requests/latest-by-stack/${encodeURIComponent(stack.name)}`);

      if (!latestRes.ok && latestRes.status !== 404) {
        throw new Error(`Failed to load deployment history: ${latestRes.status}`);
      }

      if (!latestRes.ok) {
        // No deployment history (404) — fall back to a direct CloudFormation DeleteStack using
        // the stack's own account credentials, rather than the Jenkins/CDK path.
        if (!stack.account?.Alias) {
          throw new Error('This stack has no deployment history and no associated account, so it cannot be destroyed.');
        }
        const creds = getAwsCreds(stack.account.Alias);
        if (!creds) throw new Error(`AWS credentials not configured for '${stack.account.Alias}' account. Go to Settings to set them up.`);

        const destroyRes = await sharedApiFetch(`/stacks/${encodeURIComponent(stack.documentId)}/destroy`, {
          method: 'POST',
          headers: {
            'X-AWS-Access-Key-Id': creds.accessKeyId,
            'X-AWS-Secret-Access-Key': creds.secretAccessKey,
            'X-AWS-Session-Token': creds.sessionToken,
          },
        });
        if (!destroyRes.ok) {
          const err = await destroyRes.json().catch(() => null);
          throw new Error(err?.error?.message || `Request failed: ${destroyRes.status}`);
        }
      } else {
        const creds = getAwsCreds('appsb');
        if (!creds) throw new Error(`AWS credentials not configured for 'appsb' account. Go to Settings to set them up.`);

        const latestJson = await latestRes.json();
        const entry = latestJson.data ?? latestJson;

        const res = await sharedApiFetch(`/deployment-requests/submit`, {
          method: 'POST',
          headers: {
            'X-AWS-Access-Key-Id': creds.accessKeyId,
            'X-AWS-Secret-Access-Key': creds.secretAccessKey,
            'X-AWS-Session-Token': creds.sessionToken,
          },
          body: JSON.stringify({
            projectName: entry.projectName,
            productType: entry.productType,
            accountId: entry.account?.documentId,
            envName: stack.environment?.name,
            payload: entry.payload,
            operationType: 'destroy',
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => null);
          throw new Error(err?.error?.message || `Request failed: ${res.status}`);
        }
      }

      // Reflect the teardown in the table immediately rather than waiting on refetchProject —
      // the API already set stack_status=DELETE_IN_PROGRESS synchronously as part of submit(),
      // so this just mirrors that locally without a round trip.
      setStacks(prev => prev.map(s => s.documentId === stack.documentId ? { ...s, stack_status: 'DELETE_IN_PROGRESS' } : s));

      setDeleteStackTarget(null);
      setFlash({ type: 'success', content: `Destroy request submitted for "${stack.name}"` });
      await refetchProject();
    } catch (e: any) {
      setDeleteStackError(e.message);
    } finally {
      setDeleteStackSaving(false);
    }
  };

  const openCreateEnv = () => {
    setCreateEnvName('');
    setCreateEnvError(null);
    setCreateEnvVisible(true);
  };

  // Live validation as the user types, so the Create button is disabled and the reason is shown
  // immediately rather than only after clicking Create. Empty/whitespace-only names are always
  // invalid; duplicate names (case-insensitive) are checked against this project's existing
  // environments.
  const createEnvValidationError = (() => {
    const name = createEnvName.trim();
    if (!name) return 'Environment name cannot be empty';
    if (environments.some(e => e.name.toLowerCase() === name.toLowerCase())) {
      return `An environment named "${name}" already exists for this project`;
    }
    return null;
  })();

  const handleCreateEnv = async () => {
    const name = createEnvName.trim();
    if (createEnvValidationError) {
      setCreateEnvError(createEnvValidationError);
      return;
    }
    setCreateEnvSaving(true);
    setCreateEnvError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/environments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
        body: JSON.stringify({ data: { name, isProduction: name.toLowerCase() === 'prod', project: id } }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || `API error: ${res.status}`);
      }
      await refetchProject();
      setCreateEnvVisible(false);
      setFlash({ type: 'success', content: `Environment "${name}" created` });
    } catch (e: any) {
      setCreateEnvError(e.message);
    } finally {
      setCreateEnvSaving(false);
    }
  };

  const openMoveStack = (stack: Stack) => {
    setMoveStackTarget(stack);
    setMoveStackEnv(stack.environment ? { value: stack.environment.documentId, label: stack.environment.name } : null);
  };

  const handleMoveStack = async () => {
    if (!moveStackTarget || !moveStackEnv) return;
    setMoveStackSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/stacks/${moveStackTarget.documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
        body: JSON.stringify({ data: { environment: moveStackEnv.value } }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || `API error: ${res.status}`);
      }
      await refetchProject();
      setMoveStackTarget(null);
      setFlash({ type: 'success', content: `Moved "${moveStackTarget.name}" to ${moveStackEnv.label}` });
    } catch (e: any) {
      setFlash({ type: 'error', content: e.message });
    } finally {
      setMoveStackSaving(false);
    }
  };

  const handleCreateDiagram = async (env: Environment) => {
    // The project's own `account` field is optional and may differ from (or be unset while)
    // the stacks actually deployed under this environment each have their own account — so the
    // right source of truth for "which AWS account do these resources live in" is the
    // environment's stacks themselves, not the project. Falls back to the project's account
    // only if no stack in this environment has one set either.
    const envStacks = stacks.filter(s => s.environment?.documentId === env.documentId);
    const account = envStacks.find(s => s.account)?.account ?? project?.account ?? null;

    if (!account) {
      setFlash({
        type: 'error',
        content: `No AWS account could be resolved for environment "${env.name}" — none of its stacks have an account set, and the project itself has no account linked.`,
      });
      return;
    }
    const creds = getAwsCreds(account.Alias);
    if (!creds) {
      setFlash({ type: 'error', content: `No AWS credentials configured for account "${account.Alias}". Please add credentials in Settings.` });
      return;
    }
    setDiagramLoading(true);
    setDiagramProgress({ percent: 0, label: 'Starting…' });
    setFlash(null);
    try {
      const res = await fetch(`${API_BASE_URL}/projects/${id}/environments/${env.documentId}/architecture-diagram`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`,
          'x-aws-access-key-id': creds.accessKeyId,
          'x-aws-secret-access-key': creds.secretAccessKey,
          'x-aws-session-token': creds.sessionToken,
        },
        body: JSON.stringify({ region: getDefaultRegion() }),
      });
      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error?.message || `API error: ${res.status}`);
      }

      // The endpoint streams newline-delimited JSON progress events, ending with exactly one
      // terminal `done` (carrying the generated XML + filename) or `error` line — see
      // ProgressStream in the architecture-diagram controller. Read the body incrementally so
      // the progress bar advances as each line arrives, instead of only updating once the whole
      // response has finished (which would defeat the purpose of streaming it).
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let result: { filename: string; xml: string } | null = null;

      const handleLine = (line: string) => {
        if (!line.trim()) return;
        const event = JSON.parse(line);
        if (event.type === 'progress') {
          setDiagramProgress({ percent: event.percent, label: event.label, detail: event.detail });
        } else if (event.type === 'done') {
          setDiagramProgress({ percent: 100, label: 'Done' });
          result = { filename: event.filename, xml: event.xml };
        } else if (event.type === 'error') {
          throw new Error(event.message);
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let newlineIndex;
        while ((newlineIndex = buffer.indexOf('\n')) >= 0) {
          const line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);
          handleLine(line);
        }
      }
      if (buffer.trim()) handleLine(buffer);

      if (!result) throw new Error('Diagram generation ended without a result.');
      const { filename, xml } = result;

      const blob = new Blob([xml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setFlash({ type: 'success', content: `Architecture diagram for "${env.name}" downloaded as ${filename}` });
    } catch (e: any) {
      setFlash({ type: 'error', content: e.message });
    } finally {
      setDiagramLoading(false);
      setDiagramProgress(null);
    }
  };

  if (loading) return <Spinner size="large" />;
  if (error) return <Box color="text-status-error" fontSize="heading-m">Error: {error}</Box>;
  if (!project) return <Box color="text-body-secondary">Project not found</Box>;

  const accountDesc = project.account ? `${project.account.Alias} (${project.account.accountid})` : undefined;

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup
        items={[
          { text: 'Home', href: '/' },
          { text: 'Projects', href: '/projects' },
          { text: project.name, href: '#' },
        ]}
        onFollow={e => { e.preventDefault(); navigate(e.detail.href); }}
      />
      {flash && (
        <Flashbar items={[{ type: flash.type, content: flash.content, dismissible: true, onDismiss: () => setFlash(null) }]} />
      )}
      <Header
        variant="h1"
        description={accountDesc}
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => navigate(`/create?projectId=${id}`)}>Create Product</Button>
            <ButtonDropdown
              items={[
                { id: 'attach-stacks', text: 'Attach Stacks' },
                { id: 'edit', text: 'Edit' },
                { id: 'delete', text: 'Delete' },
              ]}
              onItemClick={({ detail }) => {
                if (detail.id === 'attach-stacks') openAttachModal();
                else if (detail.id === 'edit') setEditVisible(true);
                else if (detail.id === 'delete') setDeleteVisible(true);
              }}
              loading={attachLoading}
            >
              Actions
            </ButtonDropdown>
          </SpaceBetween>
        }
      >
        {project.name}
      </Header>
      <Container header={<Header variant="h2">Overview</Header>}>
        <ColumnLayout columns={2} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Description</Box>
            <Box>{project.description ? project.description : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Jira epics</Box>
            {(project.jira_epics?.length ?? 0) === 0 ? (
              <Box>—</Box>
            ) : (
              <SpaceBetween size="xxs">
                {project.jira_epics.map((epic: any, index: number) => (
                  <div key={`epic-${index}`}>
                    <a href={epic.url} target="_blank" rel="noopener noreferrer">{epic.title}</a>
                  </div>
                ))}
              </SpaceBetween>
            )}
          </div>
          <div>
            <Box variant="awsui-key-label">Sciforma links</Box>
            {(project.sciforma_links?.length ?? 0) === 0 ? (
              <Box>—</Box>
            ) : (
              <SpaceBetween size="xxs">
                {project.sciforma_links.map((link: any, index: number) => (
                  <div key={`sciforma-${index}`}>
                    <a href={link.url} target="_blank" rel="noopener noreferrer">{link.title}</a>
                  </div>
                ))}
              </SpaceBetween>
            )}
          </div>
          <div>
            <Box variant="awsui-key-label">External links</Box>
            {(project.external_links?.length ?? 0) === 0 ? (
              <Box>—</Box>
            ) : (
              <SpaceBetween size="xxs">
                {project.external_links.map((link: any, index: number) => (
                  <div key={`external-${index}`}>
                    <a href={link.url} target="_blank" rel="noopener noreferrer">{link.title}</a>
                  </div>
                ))}
              </SpaceBetween>
            )}
          </div>
        </ColumnLayout>
      </Container>
      <Container header={<Header variant="h2" description="Group this project's stacks by deployment environment.">Environments</Header>}>
        <SpaceBetween size="m">
          <EnvironmentCards
            environments={environments.map(e => ({
              documentId: e.documentId,
              name: e.name,
              isProduction: e.isProduction,
              stackCount: stacks.filter(s => s.environment?.documentId === e.documentId).length,
            }))}
            selectedId={selectedEnvId}
            onSelect={setSelectedEnvId}
            onCreate={openCreateEnv}
          />
          {selectedEnvId && (
            <Box>
              <SpaceBetween size="xs">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    onClick={() => openAttachModal(environments.find(e => e.documentId === selectedEnvId))}
                    iconName="search"
                  >
                    Locate stacks for {environments.find(e => e.documentId === selectedEnvId)?.name}
                  </Button>
                  <Button
                    onClick={() => {
                      const env = environments.find(e => e.documentId === selectedEnvId);
                      if (env) handleCreateDiagram(env);
                    }}
                    iconName="download"
                    loading={diagramLoading}
                  >
                    Create architecture diagram
                  </Button>
                </SpaceBetween>
                {diagramProgress && (
                  <Box>
                    <ProgressBar
                      value={diagramProgress.percent}
                      label="Generating architecture diagram"
                      description={diagramProgress.detail}
                      additionalInfo={diagramProgress.label}
                    />
                  </Box>
                )}
              </SpaceBetween>
            </Box>
          )}
        </SpaceBetween>
      </Container>
      <Modal
        visible={createEnvVisible}
        onDismiss={() => setCreateEnvVisible(false)}
        header="Create environment"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setCreateEnvVisible(false)}>Cancel</Button>
              <Button
                variant="primary"
                onClick={handleCreateEnv}
                loading={createEnvSaving}
                disabled={!!createEnvValidationError}
              >
                Create
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Form>
          <FormField
            label="Name"
            description="Choose a common environment or type a custom name for this project."
            errorText={createEnvError ?? (createEnvName ? createEnvValidationError : null)}
          >
            <Autosuggest
              value={createEnvName}
              onChange={({ detail }) => { setCreateEnvName(detail.value); setCreateEnvError(null); }}
              options={COMMON_ENV_NAMES.filter(n => !environments.some(e => e.name.toLowerCase() === n.toLowerCase())).map(n => ({ value: n }))}
              enteredTextLabel={value => `Use custom name "${value}"`}
              placeholder="Prod"
              empty="No matching suggestions — you can still enter a custom name"
              ariaLabel="Environment name"
            />
          </FormField>
        </Form>
      </Modal>
      <Modal
        visible={!!moveStackTarget}
        onDismiss={() => setMoveStackTarget(null)}
        header={`Move "${moveStackTarget?.name ?? ''}" to another environment`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setMoveStackTarget(null)}>Cancel</Button>
              <Button variant="primary" onClick={handleMoveStack} loading={moveStackSaving} disabled={!moveStackEnv}>Move</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Form>
          <FormField label="Environment" description="Moving a stack only changes which environment it's grouped under here — it does not rename or redeploy the underlying CloudFormation stack.">
            <Select
              selectedOption={moveStackEnv}
              onChange={({ detail }) => setMoveStackEnv(detail.selectedOption as any)}
              options={environments.map(e => ({ value: e.documentId, label: e.name }))}
              placeholder="Select environment"
            />
          </FormField>
        </Form>
      </Modal>
      <Modal
        visible={attachModalVisible}
        onDismiss={() => setAttachModalVisible(false)}
        header="Attach Stacks"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setAttachModalVisible(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleAttachStacks}>Submit</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Form>
          <SpaceBetween size="m">
            <FormField label="Account" description="Select the AWS account to search for stacks">
              <Select
                selectedOption={attachAccount}
                onChange={({ detail }) => setAttachAccount(detail.selectedOption as any)}
                options={attachAccountOptions}
                placeholder="Select account"
                filteringType="auto"
              />
            </FormField>
            <FormField label="Project scope" description="Every search is always scoped to this project's ICLProject tag, so stacks belonging to other projects are never matched or attached.">
              <Input value={`ICLProject = ${project.name}`} disabled />
            </FormField>
            <FormField label="Environment (optional)" description="Narrow the search to a specific environment's ICLEnv tag within this project. Leave blank to search across all of this project's environments.">
              <Select
                selectedOption={attachEnvFilter ? { value: attachEnvFilter.documentId, label: attachEnvFilter.name } : null}
                onChange={({ detail }) => {
                  const match = environments.find(e => e.documentId === detail.selectedOption.value);
                  setAttachEnvFilter(match ?? null);
                }}
                options={[{ value: '', label: 'All environments' }, ...environments.map(e => ({ value: e.documentId, label: e.name }))]}
                placeholder="All environments"
              />
            </FormField>
          </SpaceBetween>
        </Form>
      </Modal>
      <Tabs
        onChange={({ detail }) => {
          setActiveTabId(detail.activeTabId);
          if (detail.activeTabId === 'stakeholders') fetchStakeholders();
          if (detail.activeTabId === 'products') fetchProducts();
          if (detail.activeTabId === 'deployments') fetchDeployments();
          if (detail.activeTabId === 'jira') fetchJira();
        }}
        tabs={[
          {
            id: 'stacks',
            label: 'Stacks',
            content: (() => {
              const filteredStacks = selectedEnvId ? stacks.filter(s => s.environment?.documentId === selectedEnvId) : stacks;
              const selectedEnvName = environments.find(e => e.documentId === selectedEnvId)?.name;
              return (
                <Table
                  variant="embedded"
                  items={filteredStacks}
                  columnDefinitions={[
                    {
                      id: 'name', header: 'Name',
                      cell: (i) => <Link to={`/stacks/${i.documentId}`}>{i.name}</Link>,
                    },
                    { id: 'environment', header: 'Environment', cell: i => i.environment ? <Badge color={i.environment.isProduction ? 'red' : 'blue'}>{i.environment.name}</Badge> : <Box color="text-body-secondary">—</Box> },
                    { id: 'status', header: 'Status', cell: i => <StatusIndicator type={statusType(i.stack_status)}>{i.stack_status}</StatusIndicator> },
                    { id: 'region', header: 'Region', cell: i => i.Region },
                    { id: 'arn', header: 'ARN', cell: i => i.arn, maxWidth: 300 },
                    {
                      id: 'actions', header: 'Actions', width: 140, minWidth: 140,
                      cell: (i) => (
                        <SpaceBetween direction="horizontal" size="xs">
                          <Button
                            variant="icon"
                            iconName="edit"
                            ariaLabel={`Edit / Redeploy ${i.name}`}
                            onClick={() => navigate(`/projects/${id}/stacks/${i.documentId}/edit`)}
                          />
                          <Button
                            variant="icon"
                            iconName="folder-open"
                            ariaLabel={`Move ${i.name} to another environment`}
                            onClick={() => openMoveStack(i)}
                          />
                          <Button
                            variant="icon"
                            iconName="close"
                            ariaLabel={`Delete ${i.name}`}
                            onClick={() => { setDeleteStackError(null); setDeleteStackTarget(i); }}
                          />
                        </SpaceBetween>
                      ),
                    },
                  ]}
                  empty={<Box textAlign="center" color="text-body-secondary">No stacks</Box>}
                  header={
                    <Header counter={`(${filteredStacks.length})`} description={selectedEnvName ? `Filtered to ${selectedEnvName}` : undefined}>
                      Stacks
                    </Header>
                  }
                />
              );
            })(),
          },
          {
            id: 'products',
            label: 'Products',
            content: productsLoading ? <Spinner /> : (
              <Table
                variant="embedded"
                items={products}
                columnDefinitions={[
                  { id: 'name', header: 'Name', cell: (i: any) => <Link to={`/products/${i.type}/${i.documentId}`}>{i.name}</Link> },
                  { id: 'type', header: 'Type', cell: (i: any) => i.typeLabel },
                  { id: 'stack', header: 'Stack', cell: (i: any) => i.stackName || '—' },
                ]}
                empty={<Box textAlign="center" color="text-body-secondary">No products</Box>}
                header={<Header counter={`(${products.length})`}>Products</Header>}
              />
            ),
          },
          {
            id: 'deployments',
            label: 'Deployments',
            content: deploymentsLoading ? <Spinner /> : (
              <SpaceBetween size="l">
                <Table
                  variant="embedded"
                  items={deployments}
                  onRowClick={({ detail }) => setSelectedDeployment(detail.item)}
                  columnDefinitions={[
                    { id: 'status', header: 'Status', cell: (i: any) => <StatusIndicator type={{ success: 'success', failed: 'error', running: 'in-progress', submitted: 'info', pending: 'warning', cancelled: 'stopped' }[i.status as string] as any ?? 'info'}>{i.status}</StatusIndicator> },
                    { id: 'productType', header: 'Product Type', cell: (i: any) => i.productType },
                    { id: 'requestedBy', header: 'Requested By', cell: (i: any) => i.requestedBy },
                    { id: 'submittedAt', header: 'Submitted At', cell: (i: any) => new Date(i.submittedAt).toLocaleString() },
                    { id: 'completedAt', header: 'Completed At', cell: (i: any) => i.completedAt ? new Date(i.completedAt).toLocaleString() : '—' },
                    { id: 'build', header: 'Build', cell: (i: any) => i.source === 'fm' ? (i.executionId || '—') : (i.jenkinsJobUrl ? <a href={i.jenkinsJobUrl} target="_blank" rel="noopener noreferrer">{i.jenkinsBuildNumber}</a> : '—') },
                    { id: 'message', header: 'Message', cell: (i: any) => i.statusMessage ?? '—' },
                  ]}
                  empty={<Box textAlign="center" color="text-body-secondary">No deployments</Box>}
                  header={<Header counter={`(${deployments.length})`} actions={
                    <SpaceBetween direction="horizontal" size="xs">
                      <Toggle checked={autoRefresh} onChange={({ detail }) => setAutoRefresh(detail.checked)}>Auto-refresh</Toggle>
                      <Button iconName="refresh" onClick={refreshDeployments} loading={deploymentsLoading}>Refresh</Button>
                    </SpaceBetween>
                  }>Deployments</Header>}
                />
                <Modal
                  visible={!!selectedDeployment}
                  onDismiss={() => setSelectedDeployment(null)}
                  header="Deployment Details"
                  size="large"
                >
                  {selectedDeployment && (
                    <SpaceBetween size="m">
                      <div><Box variant="awsui-key-label">Status</Box><StatusIndicator type={{ success: 'success', failed: 'error', running: 'in-progress', submitted: 'info', pending: 'warning', cancelled: 'stopped' }[selectedDeployment.status as string] as any ?? 'info'}>{selectedDeployment.status}</StatusIndicator></div>
                      <div><Box variant="awsui-key-label">Product Type</Box><Box>{selectedDeployment.productType}</Box></div>
                      <div><Box variant="awsui-key-label">Project Name</Box><Box>{selectedDeployment.source === 'fm' ? (selectedDeployment.projectSlug ?? '—') : selectedDeployment.projectName}</Box></div>
                      <div><Box variant="awsui-key-label">Requested By</Box><Box>{selectedDeployment.requestedBy ?? '—'}</Box></div>
                      <div><Box variant="awsui-key-label">Submitted At</Box><Box>{selectedDeployment.submittedAt ? new Date(selectedDeployment.submittedAt).toLocaleString() : '—'}</Box></div>
                      <div><Box variant="awsui-key-label">Completed At</Box><Box>{selectedDeployment.completedAt ? new Date(selectedDeployment.completedAt).toLocaleString() : '—'}</Box></div>
                      {selectedDeployment.source === 'fm' ? (
                        <div><Box variant="awsui-key-label">SSM Execution ID</Box><Box>{selectedDeployment.executionId ?? '—'}</Box></div>
                      ) : (
                        <div><Box variant="awsui-key-label">Jenkins Build</Box><Box>{selectedDeployment.jenkinsJobUrl ? <a href={selectedDeployment.jenkinsJobUrl} target="_blank" rel="noopener noreferrer">Build #{selectedDeployment.jenkinsBuildNumber}</a> : '—'}</Box></div>
                      )}
                      <div><Box variant="awsui-key-label">Status Message</Box><Box>{selectedDeployment.statusMessage ?? '—'}</Box></div>
                      {selectedDeployment.source === 'fm' ? (
                        <div><Box variant="awsui-key-label">Parameters</Box><Box><pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>{JSON.stringify(selectedDeployment.parameters, null, 2)}</pre></Box></div>
                      ) : (
                        <div><Box variant="awsui-key-label">Payload</Box><Box><pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>{JSON.stringify(selectedDeployment.payload, null, 2)}</pre></Box></div>
                      )}
                    </SpaceBetween>
                  )}
                </Modal>
              </SpaceBetween>
            ),
          },
          {
            id: 'stakeholders',
            label: 'Stakeholders',
            content: shLoading ? <Spinner /> : shError ? <Box color="text-status-error">Error: {shError}</Box> : (
              <SpaceBetween size="l">
                <Table
                  variant="embedded"
                  header={<Header counter={groups ? `(${groups.length})` : ''} actions={<Button onClick={openAddGroup}>Add group</Button>}>Stakeholder Groups</Header>}
                  items={groups ?? []}
                  columnDefinitions={[
                    { id: 'name', header: 'Name', cell: i => i.name },
                    { id: 'desc', header: 'Description', cell: i => i.description || '—' },
                    { id: 'members', header: 'Members', cell: i => i.stakeholders?.length ?? 0 },
                  ]}
                  empty={<Box textAlign="center" color="text-body-secondary">No groups</Box>}
                />
                <Table
                  variant="embedded"
                  header={<Header counter={stakeholders ? `(${stakeholders.length})` : ''} actions={<Button onClick={openAddStakeholder}>Add stakeholder</Button>}>Stakeholders</Header>}
                  items={stakeholders ?? []}
                  columnDefinitions={[
                    { id: 'name', header: 'Name', cell: i => `${i.firstName} ${i.lastName}` },
                    { id: 'email', header: 'Email', cell: i => i.email },
                    { id: 'dept', header: 'Department', cell: i => i.department || '—' },
                    { id: 'role', header: 'Role', cell: i => i.role || '—' },
                  ]}
                  empty={<Box textAlign="center" color="text-body-secondary">No stakeholders</Box>}
                />
              </SpaceBetween>
            ),
          },
          {
            id: 'jira',
            label: 'Jira',
            content: jiraUnconfigured ? (
              <Alert type="info" header="Jira not configured">
                Configure a Jira base URL and Personal Access Token in <Link to="/settings">Settings</Link> to view issues.
              </Alert>
            ) : jiraLoading ? <Spinner /> : jiraError ? (
              <Alert type="error" header="Failed to load Jira issues">{jiraError}</Alert>
            ) : (
              <Table
                variant="embedded"
                items={jira}
                columnDefinitions={[
                  { id: 'key', header: 'Key', cell: (i: JiraIssue) => <a href={`${jiraBaseUrl}/browse/${i.key}`} target="_blank" rel="noopener noreferrer">{i.key}</a> },
                  { id: 'summary', header: 'Summary', cell: (i: JiraIssue) => i.summary },
                  { id: 'status', header: 'Status', cell: (i: JiraIssue) => i.status ?? '—' },
                  { id: 'assignee', header: 'Assignee', cell: (i: JiraIssue) => i.assignee ?? '—' },
                  { id: 'type', header: 'Type', cell: (i: JiraIssue) => i.type ?? '—' },
                  { id: 'updated', header: 'Updated', cell: (i: JiraIssue) => i.updated ? new Date(i.updated).toLocaleString() : '—' },
                ]}
                empty={<Box textAlign="center" color="text-body-secondary">No matching Jira issues</Box>}
                header={
                  <Header
                    counter={`(${jira.length})`}
                    description={jiraTotal > jira.length ? `Showing first ${jira.length} of ${jiraTotal} matching issues (truncated to 50).` : undefined}
                  >
                    Jira issues
                  </Header>
                }
              />
            ),
          },
        ]}
      />
      <Modal
        visible={addShVisible}
        onDismiss={() => setAddShVisible(false)}
        header="Add Stakeholders"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setAddShVisible(false)}>Cancel</Button>
              <Button variant="primary" onClick={linkStakeholders} loading={addShLoading} disabled={!selectedSh.length}>Add selected</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Table
          selectionType="multi"
          selectedItems={selectedSh}
          onSelectionChange={({ detail }) => setSelectedSh(detail.selectedItems as Stakeholder[])}
          trackBy="documentId"
          items={allStakeholders.filter(s => !(stakeholders ?? []).some(e => e.documentId === s.documentId))}
          columnDefinitions={[
            { id: 'name', header: 'Name', cell: i => `${i.firstName} ${i.lastName}` },
            { id: 'email', header: 'Email', cell: i => i.email },
            { id: 'role', header: 'Role', cell: i => i.role },
          ]}
          empty={<Box textAlign="center" color="text-body-secondary">No available stakeholders</Box>}
        />
      </Modal>
      <Modal
        visible={addGrVisible}
        onDismiss={() => setAddGrVisible(false)}
        header="Add Stakeholder Groups"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setAddGrVisible(false)}>Cancel</Button>
              <Button variant="primary" onClick={linkGroups} loading={addShLoading} disabled={!selectedGr.length}>Add selected</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Table
          selectionType="multi"
          selectedItems={selectedGr}
          onSelectionChange={({ detail }) => setSelectedGr(detail.selectedItems as StakeholderGroup[])}
          trackBy="documentId"
          items={allGroups.filter(g => !(groups ?? []).some(e => e.documentId === g.documentId))}
          columnDefinitions={[
            { id: 'name', header: 'Name', cell: i => i.name },
            { id: 'desc', header: 'Description', cell: i => i.description || '—' },
          ]}
          empty={<Box textAlign="center" color="text-body-secondary">No available groups</Box>}
        />
      </Modal>
      <ProjectFormModal
        visible={editVisible}
        mode="edit"
        saving={editSaving}
        initial={{
          name: project.name,
          description: project.description ?? '',
          epics: (project.jira_epics ?? []).map((e: any) => ({ title: e.title ?? '', url: e.url ?? '' })),
          sciforma_links: (project.sciforma_links ?? []).map((l: any) => ({ title: l.title ?? '', url: l.url ?? '' })),
          external_links: (project.external_links ?? []).map((l: any) => ({ title: l.title ?? '', url: l.url ?? '' })),
        }}
        onDismiss={() => setEditVisible(false)}
        onSubmit={handleEditProject}
      />
      <DeleteConfirmModal
        visible={deleteVisible}
        header="Delete project"
        itemName={project.name}
        loading={deleteSaving}
        onDismiss={() => setDeleteVisible(false)}
        onConfirm={handleDeleteProject}
      />
      <DeleteStackModal
        visible={!!deleteStackTarget}
        stackName={deleteStackTarget?.name ?? ''}
        loading={deleteStackSaving}
        error={deleteStackError}
        onDismiss={() => { setDeleteStackTarget(null); setDeleteStackError(null); }}
        onConfirm={handleDestroyStack}
      />
    </SpaceBetween>
  );
}
